import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import ApiUrl from "../Environment/ApiUrl";
import "../../assets/css/SalesBill.css";

function RetailerSmallBill() {
  const navigate = useNavigate();
  const { retailer_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retailersData, setRetailersData] = useState(null);

  useEffect(() => {
    if (retailer_id) {
      fetchRetailersData();
    }
  }, [retailer_id]);

  const fetchRetailersData = async () => {
    try {
      setLoading(true);
      console.log("Fetching for retailer_id:", retailer_id);
      
      const response = await axios.get(
        `${ApiUrl}/Api/retailer_details.php?retailer_id=${retailer_id}`
      );
      
      console.log("API response:", response.data);

      if (!response.data?.data || !Array.isArray(response.data.data)) {
        throw new Error("Invalid API response format");
      }

      const filteredData = response.data.data.find(
        (item) => item.retailer_id === retailer_id
      );

      if (filteredData) {
        setRetailersData(filteredData);
      } else {
        setError(`No retailer data found for ID: ${retailer_id}`);
      }
    } catch (error) {
      console.error("API error:", error);
      setError("Failed to fetch retailer data");
    } finally {
      setLoading(false);
    }
  };

  // ✅ CORRECTED numberToWords function - handles both Rupees and Grams
  const numberToWords = (num, isPure = false) => {
    if (isNaN(num) || num === null || num === undefined) return "";
    if (num === 0) return isPure ? "Zero Grams" : "Zero Rupees Only";

    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const numToWord = (n) => {
      if (n === 0) return "";
      if (n < 20) return ones[n];
      if (n < 100)
        return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
      if (n < 1000)
        return (
          ones[Math.floor(n / 100)] +
          " Hundred" +
          (n % 100 ? " and " + numToWord(n % 100) : "")
        );
      if (n < 100000)
        return (
          numToWord(Math.floor(n / 1000)) +
          " Thousand" +
          (n % 1000 ? " " + numToWord(n % 1000) : "")
        );
      if (n < 10000000)
        return (
          numToWord(Math.floor(n / 100000)) +
          " Lakh" +
          (n % 100000 ? " " + numToWord(n % 100000) : "")
        );
      return (
        numToWord(Math.floor(n / 10000000)) +
        " Crore" +
        (n % 10000000 ? " " + numToWord(n % 10000000) : "")
      );
    };

    const decimalToWord = (decimalPart) => {
      if (decimalPart === 0) return "";
      const decStr = decimalPart.toString().padEnd(2, "0").slice(0, 2);
      const decValue = parseInt(decStr);

      if (decValue === 0) return "";

      // For Pure (grams), the decimal represents milligrams
      if (isPure) {
        if (decValue < 20)
          return " and " + ones[decValue] + " Milligram";
        return (
          " and " +
          tens[Math.floor(decValue / 10)] +
          (decValue % 10 ? " " + ones[decValue % 10] : "") +
          " Milligram"
        );
      }

      // For Rupees, decimal represents paise
      if (decValue < 20)
        return " and " + ones[decValue] + " Paise";
      return (
        " and " +
        tens[Math.floor(decValue / 10)] +
        (decValue % 10 ? " " + ones[decValue % 10] : "") +
        " Paise"
      );
    };

    const parts = num.toString().split(".");
    const integerPart = parseInt(parts[0]) || 0;
    const decimalPart = parts[1]
      ? parseInt(parts[1].padEnd(2, "0").slice(0, 2))
      : 0;

    let result = integerPart === 0 ? "Zero" : numToWord(integerPart);
    
    if (isPure) {
      result += integerPart === 1 ? " Gram" : " Grams";
    } else {
      result += integerPart === 1 ? " Rupee" : " Rupees";
    }
    
    if (decimalPart > 0) result += decimalToWord(decimalPart);
    
    return result + (isPure ? "" : " Only");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  if (loading) return <div className="d-flex justify-content-center p-4">Loading retailer data...</div>;
  if (error) return <div className="d-flex justify-content-center p-4 text-danger">{error}</div>;
  if (!retailersData) return <div className="d-flex justify-content-center p-4">No retailer data available</div>;

  // Extract data from API response
  const retailerInfo = retailersData.retailer_details || retailersData.supplier_details || {};
  
  const retailerName = retailerInfo?.name || "N/A";
  const retailerMobile = retailerInfo?.mobile || "N/A";
  const retailerAddress = retailerInfo?.address || "N/A";
  const supplierId = retailersData.supplier_id || retailerInfo?.retailer_id || "N/A";

  // Calculate totals
  const totalQuantity = retailersData.items?.reduce(
    (total, item) => total + (parseInt(item.quantity) || 0), 0
  ) || 0;
  
  const totalWeight = retailersData.items?.reduce(
    (total, item) => total + (parseFloat(item.weight) || 0), 0
  ) || 0;
  
  const totalPure = retailersData.items?.reduce(
    (total, item) => total + (parseFloat(item.pure) || 0), 0
  ) || 0;

  // Get values based on return type
  const returnType = retailersData.return_type || "Amount";
  const openingBalance = parseFloat(retailersData.opening) || 0;
  const currentBalance = parseFloat(retailersData.current) || 0;
  const closingBalance = parseFloat(retailersData.close) || 0;
  
  // For Amount type - use amount/paid/enter_amount fields
  const enterAmount = parseFloat(retailersData.enter_amount) || 0;
  const paidAmount = parseFloat(retailersData.amount) || parseFloat(retailersData.paid) || 0;
  
  // For Pure type - use pure field (in grams)
  const pureReturn = parseFloat(retailersData.pure) || 0;

  // Determine what to display based on return type
  const displayTotalPaid = returnType === "Amount" ? paidAmount : pureReturn;
  const displayBalance = returnType === "Amount" ? closingBalance : closingBalance;
  const displayEnterAmount = returnType === "Amount" ? enterAmount : totalPure;

  const formattedDate = formatDate(retailersData.retailer_date);

  return (
    <div className="bill-container">
      <button className="print-btn no-print" onClick={() => window.print()}>
        Print Bill
      </button>
      <button className="cancel-btn no-print" onClick={() => navigate("/retailers")}>
        Cancel
      </button>

      {/* Header */}
      <div className="header-section d-flex justify-content-between align-items-center">
        <div className="logo-container">
          <img
            src={require("../../assets/images/Final logo c.png")}
            alt="TIRUPUR Logo"
            className="logo"
            style={{ width: "100px" }}
          />
        </div>
        <div className="company-info text-center">
          <h4 className="mb-1">TIRUPUR JEWELLERY</h4>
          <p className="mb-0">3/1, Thandan St, Shevapet, Salem, Tamil Nadu 636002</p>
          <p className="mb-0">Ph: 04272233807</p>
        </div>
        <div className="invoice-title">
          <p className="m-0 fw-bold">RETAILER BILL</p>
        </div>
      </div>

      <hr className="divider" />

      {/* Customer + Invoice Info */}
      <div className="details-section">
        <div className="customer-info">
          <div className="customer-details">
            <p><strong>{retailerName}</strong></p>
            <p>{retailerAddress}</p>
            <p>Cell: {retailerMobile}</p>
            <p>Customer ID: {supplierId}</p>
          </div>
        </div>
        <div className="invoice-info">
          <div className="info-row"><span>Date</span><span>: {formattedDate}</span></div>
          <div className="info-row"><span>Bill No</span><span>: {retailersData.retailer_id}</span></div>
          <div className="info-row"><span>Return Type</span><span>: {returnType}</span></div>
          <div className="info-row"><span>Manager</span><span>: {retailersData.manager || "N/A"}</span></div>
          <div className="info-row"><span>Staff</span><span>: {retailersData.staff || "N/A"}</span></div>
        </div>
      </div>

      {/* Items Table */}
      <div className="table-section">
        <table className="bill-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Product</th>
              <th>Qty</th>
              <th>Weight(g)</th>
              <th>Purity(%)</th>
              <th>Pure(g)</th>
              <th>Adjustment</th>
            </tr>
          </thead>
          <tbody>
            {retailersData.items && retailersData.items.length > 0 ? (
              retailersData.items.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>
                    {item.item_name_tamil 
                      ? `${item.item_name_english || item.item} / ${item.item_name_tamil}`
                      : (item.item_name_english || item.item)}
                    {item.other_name ? ` (${item.other_name})` : ""}
                  </td>
                  <td>{item.quantity || 0}</td>
                  <td>{parseFloat(item.weight || 0).toFixed(3)}</td>
                  <td>{parseFloat(item.percentage || 0).toFixed(2)}</td>
                  <td>{parseFloat(item.pure || 0).toFixed(3)}</td>
                  <td className="text-start">
                    <span className={parseFloat(item.adjustment) > 0 ? "text-success" : parseFloat(item.adjustment) < 0 ? "text-danger" : ""}>
                      {parseFloat(item.adjustment) > 0 ? "+" : ""}
                      {parseFloat(item.adjustment || 0).toFixed(3)}
                    </span>
                   </td>
                 </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center">No items found</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="table-footer">
              <th colSpan="2">Total</th>
              <th>{totalQuantity}</th>
              <th>{totalWeight.toFixed(3)}</th>
              <th>-</th>
              <th>{totalPure.toFixed(3)}</th>
              <th>-</th>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Totals + Terms */}
      <div className="details-section">
        <div className="customer-info">
          <div className="amount-in-words">
            <p><strong>Amount in Words:</strong></p>
            <p>
              {returnType === "Amount" 
                ? numberToWords(displayTotalPaid, false)
                : numberToWords(displayTotalPaid, true)}
            </p>
          </div>
          <p className="mt-2">*Terms &amp; Conditions</p>
          <p className="paragraph-cont">
            Interest may apply on late payments. Return of amount as cash or gold upon closure.
          </p>
        </div>
        <div className="invoice-info">
          <div className="totals-section">
            <div className="totals-grid">
              <div className="total-row">
                <span>Total Pure (g)</span>
                <span>{totalPure.toFixed(3)}</span>
              </div>
              
              {returnType === "Amount" && (
                <>
                  <div className="total-row">
                    <span>Enter Amount (₹)</span>
                    <span>₹{displayEnterAmount.toFixed(2)}</span>
                  </div>
                  <div className="total-row highlight">
                    <span>Paid (₹)</span>
                    <span>₹{displayTotalPaid.toFixed(2)}</span>
                  </div>
                </>
              )}
              
              {returnType === "Pure" && (
                <div className="total-row highlight">
                  <span>Pure Return (g)</span>
                  <span>{displayTotalPaid.toFixed(3)}g</span>
                </div>
              )}
              
              <div className="total-row">
                <span>Balance</span>
                <span>
                  {returnType === "Amount" 
                    ? `₹${displayBalance.toFixed(2)}` 
                    : `${displayBalance.toFixed(3)}g`}
                </span>
              </div>
              
              <div className="total-row small-text">
                <span>Opening: {openingBalance.toFixed(3)}g</span>
                <span> | Current: {currentBalance.toFixed(3)}g</span>
                <span> | Closing: {closingBalance.toFixed(3)}g</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr className="divider" />

      {/* Footer */}
      <div className="footer-section">
        <div className="signatures">
          <div className="customer-sign">
            <p>Signature of the Customer</p>
          </div>
          <div>
            <p className="footer-notes">--- Thank You ---</p>
            <div className="alaan">
              <p>Developed by Aalan Tech Soft - Salem</p>
            </div>
          </div>
          <div className="company-sign">
            <p>FOR TIRUPUR JEWELLERY</p>
            <p>Authorized Signature</p>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .bill-container, .bill-container * {
            visibility: visible;
          }
          .bill-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 0;
          }
          .no-print {
            display: none !important;
          }
          @page {
            size: A5;
            margin: 0.5cm;
          }
        }
        
        .small-text {
          font-size: 9px;
          color: #666;
          margin-top: 8px;
          padding-top: 5px;
          border-top: 1px dashed #ddd;
        }
        
        .small-text span {
          display: inline;
        }
        
        .text-success {
          color: #28a745;
        }
        
        .text-danger {
          color: #dc3545;
        }
        
        .customer-details p {
          margin: 3px 0;
          word-break: break-word;
        }
        
        .info-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 5px;
          font-size: 10px;
        }
        
        .totals-grid {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        
        .total-row {
          display: flex;
          justify-content: space-between;
          font-size: 10px;
        }
        
        .total-row.highlight {
          font-weight: bold;
          color: #007bff;
        }
        
        .amount-in-words {
          font-size: 9px;
          margin-bottom: 10px;
          word-break: break-word;
        }
        
        .paragraph-cont {
          font-size: 8px;
          color: #666;
          margin-top: 5px;
        }
      `}</style>
    </div>
  );
}

export default RetailerSmallBill;